import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Truck, Menu, MapPin, Gift, ShoppingBag, User } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { DELIVERY_DELAY } from "@/lib/poster";
import { WHATSAPP_URL, WHATSAPP_DISPLAY, INSTAGRAM_URL, INSTAGRAM_HANDLE, WhatsAppIcon, InstagramIcon } from "@/components/Social";

const links = [
  { to: "/", label: "Accueil", id: "nav-home-link" },
  { to: "/modeles", label: "Nos modèles", id: "nav-models-link" },
  { to: "/configurateur", label: "Créer mon tableau", id: "nav-configurator-link" },
  { to: "/suivi", label: "Suivre ma commande", id: "nav-tracking-link" },
];

const ANNOUNCES = [
  { icon: Truck, text: `Livraison sous ${DELIVERY_DELAY}` },
  { icon: MapPin, text: "Livraison en France & en Belgique" },
  { icon: Gift, text: "Livraison offerte" },
];

const AnnouncementBar = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % ANNOUNCES.length), 3500);
    return () => clearInterval(t);
  }, []);
  const A = ANNOUNCES[i];
  return (
    <div className="grad-bar text-white text-[9px] min-[390px]:text-[10px] sm:text-xs tracking-[0.1em] sm:tracking-[0.2em] uppercase h-9 overflow-hidden px-3" data-testid="header-delivery-notice">
      <div key={i} className="announce-in h-full flex items-center justify-center gap-2" data-testid="announcement-text">
        <A.icon size={14} className="text-white" /> {A.text}
      </div>
    </div>
  );
};

export const Header = () => {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50 bg-[#F6F0EB]/90 backdrop-blur-md border-b border-[#DDD3C6]">
      <AnnouncementBar />
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-10 h-20 grid grid-cols-[44px_minmax(0,1fr)_96px] items-center">
        <button type="button" onClick={() => setOpen(true)} aria-label="Ouvrir le menu" data-testid="menu-drawer-button"
          className="w-11 h-11 rounded-full border border-[#CDC0B0] flex items-center justify-center text-[#3A322B] hover:bg-white transition-colors duration-200">
          <Menu size={20} />
        </button>
        <Link to="/" className="flex flex-col items-center leading-none" data-testid="logo-link">
          <span className="font-display text-xl sm:text-2xl lg:text-3xl text-[#3A322B] tracking-[0.12em]">MS SIGNATURE</span>
          <span className="text-[8px] sm:text-[10px] tracking-[0.2em] sm:tracking-[0.35em] uppercase text-[#9A8C7D] mt-1">Tableaux personnalisés</span>
        </Link>
        <div className="flex items-center justify-end gap-2">
          <a href={`${process.env.REACT_APP_SHOPIFY_STORE_URL}/account`} aria-label="Mon compte Shopify" title="Mon compte" data-testid="header-account-link"
            className="w-11 h-11 rounded-full border border-[#CDC0B0] flex items-center justify-center text-[#3A322B] hover:bg-white transition-colors duration-200">
            <User size={20} />
          </a>
          <a href={`${process.env.REACT_APP_SHOPIFY_STORE_URL}/cart`} aria-label="Ouvrir le panier Shopify" title="Panier" data-testid="header-cart-link"
            className="w-11 h-11 rounded-full border border-[#CDC0B0] flex items-center justify-center text-[#3A322B] hover:bg-white transition-colors duration-200">
            <ShoppingBag size={20} />
          </a>
        </div>
      </div>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="bg-[#F6F0EB] border-[#DDD3C6] w-[300px] sm:w-[360px]" data-testid="menu-drawer">
          <SheetTitle className="font-display text-2xl tracking-[0.12em] font-normal">MS SIGNATURE</SheetTitle>
          <SheetDescription className="text-xs uppercase tracking-[0.3em] text-[#9A8C7D]">Menu</SheetDescription>
          <nav className="mt-10 flex flex-col">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} end onClick={() => setOpen(false)} data-testid={l.id}
                className={({ isActive }) => `py-4 border-b border-[#DDD3C6] font-display text-2xl transition-colors duration-200 ${isActive ? "text-[#7B5338]" : "text-[#3A322B] hover:text-[#7B5338]"}`}>
                {l.label}
              </NavLink>
            ))}
            <a href={`${process.env.REACT_APP_SHOPIFY_STORE_URL}/cart`} onClick={() => setOpen(false)} data-testid="drawer-cart-link"
              className="py-4 border-b border-[#DDD3C6] font-display text-2xl text-[#3A322B] hover:text-[#7B5338] transition-colors duration-200">Mon panier</a>
            <a href={`${process.env.REACT_APP_SHOPIFY_STORE_URL}/account`} onClick={() => setOpen(false)} data-testid="drawer-account-link"
              className="py-4 border-b border-[#DDD3C6] font-display text-2xl text-[#3A322B] hover:text-[#7B5338] transition-colors duration-200">Mon compte</a>
          </nav>
          <div className="mt-10 space-y-3 text-sm text-[#3A322B]">
            <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-[#7B5338]" data-testid="drawer-instagram-link"><InstagramIcon size={18} /> {INSTAGRAM_HANDLE}</a>
            <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-[#7B5338]" data-testid="drawer-whatsapp-link"><WhatsAppIcon size={18} /> {WHATSAPP_DISPLAY}</a>
          </div>
          <p className="mt-8 text-xs text-[#6B5F53] flex items-center gap-2"><Truck size={14} className="text-[#7B5338]" /> Livraison sous {DELIVERY_DELAY}</p>
        </SheetContent>
      </Sheet>
    </header>
  );
};
